import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Sparkles, Search, Download, Loader2 } from "lucide-react";
import { API, api, tokenStore } from "../api/client";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";

const CAT = { organizational: "Organizativo", people: "Personas", physical: "Físico", technological: "Tecnológico" };
const STATUS = { not_started: "No iniciado", in_progress: "En progreso", implemented: "Implementado", not_applicable: "No aplica" };

export default function SoA() {
  const [params] = useSearchParams();
  const framework = params.get("framework") || "iso27001";
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ status: "all", applicability: "all", category: "all", q: "" });
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const qs = new URLSearchParams({ framework });
      if (filters.status !== "all") qs.set("status", filters.status);
      if (filters.applicability !== "all") qs.set("applicability", filters.applicability);
      if (filters.category !== "all") qs.set("category", filters.category);
      if (filters.q) qs.set("q", filters.q);
      try {
        const { data } = await api.get(`/soa?${qs}`);
        if (!cancelled) setData(data);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [filters, framework]);

  if (!data) return <div className="text-slate-500">Cargando SoA...</div>;

  const exportPdf = async () => {
    setExporting(true);
    try {
      const token = tokenStore.get();
      const res = await fetch(`${API}/soa/export.pdf?framework=${framework}`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });
      if (!res.ok) throw new Error("No se pudo generar el PDF.");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `SoA_${(data.framework?.code || framework)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Declaración de Aplicabilidad exportada.");
    } catch (err) { toast.error(err.message); }
    finally { setExporting(false); }
  };

  return (
    <div data-testid="soa-page" className="max-w-7xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[20px] font-semibold tracking-tight text-slate-900">Declaración de Aplicabilidad (SoA)</h1>
          <p className="text-[14px] text-slate-500 mt-1">{data.framework?.name} · {data.total} controles</p>
        </div>
        <Button variant="outline" onClick={exportPdf} disabled={exporting} data-testid="soa-export">
          {exporting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
          Exportar SoA PDF
        </Button>
      </div>

      <div className="mt-6 flex gap-3 items-end flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9" placeholder="Buscar por código o título..." value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="soa-search" />
          </div>
        </div>
        <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
          <SelectTrigger className="w-44" data-testid="soa-filter-status"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.applicability} onValueChange={(v) => setFilters({ ...filters, applicability: v })}>
          <SelectTrigger className="w-44" data-testid="soa-filter-app"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Aplicabilidad</SelectItem>
            <SelectItem value="applicable">Aplicable</SelectItem>
            <SelectItem value="not_applicable">No aplicable</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
          <SelectTrigger className="w-44" data-testid="soa-filter-cat"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las categorías</SelectItem>
            {Object.entries(CAT).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-4 bg-white border border-slate-200 rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">Código</TableHead>
              <TableHead>Título</TableHead>
              <TableHead>Categoría</TableHead>
              <TableHead>Aplicabilidad</TableHead>
              <TableHead>Estado</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.map(({ control, implementation }) => (
              <TableRow
                key={control.code}
                onClick={() => implementation?.id && navigate(`/controls/${implementation.id}`)}
                className="cursor-pointer hover:bg-slate-50"
                data-testid={`soa-row-${control.code}`}
              >
                <TableCell className="font-medium text-slate-900">{control.code}</TableCell>
                <TableCell className="text-slate-700">{control.title}</TableCell>
                <TableCell className="text-slate-500 text-[12px]">{CAT[control.category]}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {implementation?.applicability === "not_applicable" ? (
                      <Badge variant="secondary">No aplica</Badge>
                    ) : (
                      <Badge variant="outline">Aplica</Badge>
                    )}
                    {implementation?.applicability_source === "llm_suggested" && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex items-center text-primary"><Sparkles className="h-3.5 w-3.5" /></span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-sm">
                            <div className="text-[12px]"><b>Sugerido por IA:</b> {implementation.llm_suggestion?.reasoning || implementation.applicability_justification}</div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-slate-700 text-[12px]">{STATUS[implementation?.status || "not_started"]}</TableCell>
              </TableRow>
            ))}
            {data.items.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8">Sin resultados.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
